import { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Plus, Trash2, Save } from 'lucide-react'
import { upsertFinancialModel, fetchFinancialsComparison } from 'slices/siteFinancialSlice'

type ModelType = 'acquisition' | 'operating' | 'roi'

interface LineItem {
	label: string
	amount: number | ''
	notes?: string
}

interface Assumption {
	key: string
	value: string
}

interface FinancialModelSectionProps {
	evalId: string
	siteId: string
	modelType: ModelType
	label: string
	description: string
}

const formatCurrency = (n: number) =>
	n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

/**
 * One financial model (Acquisition / Operating / ROI) for one site. Owns its
 * own local edit state so saves are scoped to a single section. Existing
 * `notes` on line items are preserved in state but not exposed for editing at
 * this width — notes remain on round-trip saves.
 */
const FinancialModelSection = ({
	evalId,
	siteId,
	modelType,
	label,
	description,
}: FinancialModelSectionProps) => {
	const dispatch = useDispatch()
	const model = useSelector((state: any) => state.siteFinancials.bySite[siteId]?.[modelType])

	const [lineItems, setLineItems] = useState<LineItem[]>([])
	const [assumptions, setAssumptions] = useState<Assumption[]>([])
	const [dirty, setDirty] = useState(false)
	const [saving, setSaving] = useState(false)

	// Sync Redux → local state when the model snapshot changes.
	useEffect(() => {
		const items: LineItem[] = Array.isArray(model?.lineItems) ? model.lineItems : []
		const assumes: Assumption[] = Array.isArray(model?.assumptions)
			? model.assumptions
			: model?.assumptions
				? Object.entries(model.assumptions).map(([key, value]) => ({
					key,
					value: String(value),
				}))
				: []
		setLineItems(items)
		setAssumptions(assumes)
		setDirty(false)
	}, [model])

	const total = useMemo(
		() => lineItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
		[lineItems],
	)

	const handleAddLineItem = () => {
		setLineItems((prev) => [...prev, { label: '', amount: '' }])
		setDirty(true)
	}
	const handleUpdateLineItem = (idx: number, patch: Partial<LineItem>) => {
		setLineItems((prev) =>
			prev.map((item, i) => (i === idx ? { ...item, ...patch } : item)),
		)
		setDirty(true)
	}
	const handleRemoveLineItem = (idx: number) => {
		setLineItems((prev) => prev.filter((_, i) => i !== idx))
		setDirty(true)
	}

	const handleAddAssumption = () => {
		setAssumptions((prev) => [...prev, { key: '', value: '' }])
		setDirty(true)
	}
	const handleUpdateAssumption = (idx: number, patch: Partial<Assumption>) => {
		setAssumptions((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)))
		setDirty(true)
	}
	const handleRemoveAssumption = (idx: number) => {
		setAssumptions((prev) => prev.filter((_, i) => i !== idx))
		setDirty(true)
	}

	const handleSave = useCallback(async () => {
		setSaving(true)
		const cleanItems = lineItems
			.filter((item) => item.label.trim() || item.amount !== '')
			.map((item) => ({
				label: item.label.trim(),
				amount: Number(item.amount) || 0,
				notes: item.notes?.trim() || undefined,
			}))
		const cleanAssumptions = assumptions.filter((a) => a.key.trim())
		try {
			await (dispatch as any)(
				upsertFinancialModel({
					evalId,
					siteId,
					modelType,
					lineItems: cleanItems,
					assumptions: cleanAssumptions,
				}),
			)
			// @ts-ignore
			dispatch(fetchFinancialsComparison(evalId))
			setDirty(false)
		} finally {
			setSaving(false)
		}
	}, [dispatch, evalId, siteId, modelType, lineItems, assumptions])

	return (
		<div className="space-y-2">
			<div className="flex items-center justify-between">
				<div>
					<h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{label}</h5>
					<p className="text-[11px] text-gray-500 mt-0.5 leading-tight">{description}</p>
				</div>
				{dirty && <span className="text-[10px] text-amber-600 shrink-0 ml-2">Unsaved</span>}
			</div>

			<div className="space-y-1">
				{lineItems.length === 0 ? (
					<p className="text-[11px] text-gray-400 text-center py-1.5 italic">No line items.</p>
				) : (
					lineItems.map((item, idx) => (
						<div key={idx} className="grid grid-cols-12 gap-1.5 items-center">
							<Input
								className="col-span-6 text-xs"
								placeholder="Label"
								value={item.label}
								onChange={(e) => handleUpdateLineItem(idx, { label: e.target.value })}
							/>
							<Input
								className="col-span-5 text-xs text-right"
								type="number"
								placeholder="Amount"
								value={item.amount}
								onChange={(e) =>
									handleUpdateLineItem(idx, {
										amount: e.target.value === '' ? '' : Number(e.target.value),
									})
								}
							/>
							<button
								type="button"
								onClick={() => handleRemoveLineItem(idx)}
								aria-label="Remove row"
								className="col-span-1 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-gray-50 justify-self-end"
							>
								<Trash2 className="w-3 h-3" />
							</button>
						</div>
					))
				)}
			</div>

			{lineItems.length > 0 && (
				<div className="flex items-center justify-between pt-1.5 border-t border-gray-200">
					<span className="text-xs font-semibold text-gray-700">Total</span>
					<span className="text-sm font-bold text-gray-900">{formatCurrency(total)}</span>
				</div>
			)}

			{assumptions.length > 0 && (
				<div className="pt-2 space-y-1">
					<h6 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
						Assumptions
					</h6>
					{assumptions.map((a, idx) => (
						<div key={idx} className="grid grid-cols-12 gap-1.5 items-center">
							<Input
								className="col-span-5 text-xs"
								placeholder="Key"
								value={a.key}
								onChange={(e) => handleUpdateAssumption(idx, { key: e.target.value })}
							/>
							<Input
								className="col-span-6 text-xs"
								placeholder="Value"
								value={a.value}
								onChange={(e) => handleUpdateAssumption(idx, { value: e.target.value })}
							/>
							<button
								type="button"
								onClick={() => handleRemoveAssumption(idx)}
								aria-label="Remove assumption"
								className="col-span-1 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-gray-50 justify-self-end"
							>
								<Trash2 className="w-3 h-3" />
							</button>
						</div>
					))}
				</div>
			)}

			<div className="flex items-center gap-1.5 flex-wrap pt-1">
				<Button variant="ghost" onClick={handleAddLineItem} className="gap-1">
					<Plus className="w-3 h-3" />
					Row
				</Button>
				<Button variant="ghost" onClick={handleAddAssumption} className="gap-1">
					<Plus className="w-3 h-3" />
					Assumption
				</Button>
				<Button
					variant="primary"
					onClick={handleSave}
					disabled={!dirty || saving}
					className="gap-1 ml-auto"
				>
					<Save className="w-3 h-3" />
					{saving ? 'Saving' : 'Save'}
				</Button>
			</div>
		</div>
	)
}

export default FinancialModelSection
